/**
 * The upload payload for one PowerSync CRUD entry, with the night's natural key
 * added to a `rate_calendar` edit.
 *
 * A night's row id does not hold still. When a device writes a night it could
 * not see, the server keeps the night under the id that device minted, and the
 * id any other device synced stops naming a row. A PowerSync PATCH carries only
 * the columns that changed, so an edit another device has already queued names
 * nothing but that id — and without more, the server cannot find the night and
 * rejects the edit, which the host sees revert. Sending the night's
 * `(ratePlanId, date)` with it lets the server find the night by the key no id
 * change moves.
 *
 * Read from the local row at upload time, which still holds them: PowerSync does
 * not apply downloaded changes while this device has writes waiting to upload.
 * A row the local database no longer holds sends the edit as it was.
 */

/** The parts of a PowerSync `CrudEntry` this reads. */
interface UploadEntry {
  id: string;
  op: string;
  opData?: Record<string, unknown>;
  table: string;
}

/** The part of the local PowerSync database this reads. */
interface LocalDatabase {
  getOptional: (sql: string, parameters: unknown[]) => Promise<unknown>;
}

const NIGHT_KEY_SQL = "SELECT ratePlanId, date FROM rate_calendar WHERE id = ?";

export async function uploadDataFor(
  entry: UploadEntry,
  database: LocalDatabase
): Promise<Record<string, unknown>> {
  const data = entry.opData ?? {};
  const isNightEdit = entry.table === "rate_calendar" && entry.op === "PATCH";
  if (!isNightEdit) {
    return data;
  }
  const night = (await database.getOptional(NIGHT_KEY_SQL, [entry.id])) as {
    date?: unknown;
    ratePlanId?: unknown;
  } | null;
  const { date, ratePlanId } = night ?? {};
  const hasKey = typeof date === "string" && typeof ratePlanId === "string";
  if (!hasKey) {
    return data;
  }
  return { ...data, date, ratePlanId };
}
